import React, { useState, useEffect, Suspense } from "react";
import {
    FileText,
    Search,
    Filter,
    Download,
    Eye,
    Calendar,
    User,
    FolderOpen,
    File,
    FileImage,
    FileVideo,
    FileAudio,
    FileArchive,
    SortAsc,
    SortDesc,
    Grid3X3,
    List,
    MoreHorizontal,
    RefreshCw,
    Upload,
    Star,
    Share2,
    Trash2,
    Edit,
    Lock,
    Unlock,
} from "lucide-react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import { useLanguage } from "@/contexts/LanguageContext";
import { useUserInfo } from "@/hooks/useUserInfo";
import { getMyDocument, getSummary } from "@/utils/pasUtils.js";


interface Document {
    file_name: string,
    attachment_type: string,
    content_base64: string,
    file_url: string,
}

const MyDocuments: React.FC = () => {
    const VITE_USERNAMET = import.meta.env.VITE_USERNAMET
    const VITE_PASSWORD = import.meta.env.VITE_PASSWORD

    const { t } = useLanguage();
    const { fullName, email, profile } = useUserInfo();

    const [documents, setDocuments] = useState<Document[]>([]);
    const [filteredDocuments, setFilteredDocuments] = useState<Document[]>([]);
    const [loading, setLoading] = useState(true);
    const [viewMode, setViewMode] = useState<"grid" | "list">("grid");

    const [data, setData] = useState({
        profile: null,
        loading: true,
        error: null

    });

    useEffect(() => {
        let isMounted = true;

        const fetchSalaryData = async () => {
            try {
                setLoading(true);

                const summaryResponse = await getSummary(
                    VITE_USERNAMET,
                    VITE_PASSWORD,
                    email
                );

                if (!isMounted) return;

                setData({
                    profile: summaryResponse.profile,
                    loading: false,
                    error: null,
                });

                console.log("Profile:", summaryResponse.profile);

                const documentResponse = await getMyDocument(
                    VITE_USERNAMET,
                    VITE_PASSWORD,
                    summaryResponse.profile?.name
                );

                if (!isMounted) return;

                setDocuments(documentResponse);
                setFilteredDocuments(documentResponse);
                console.log("Documents:", documentResponse);
            } catch (err) {
                if (isMounted) {
                    setData({
                        profile: null,
                        loading: false,
                        error: err.message,
                    });
                }
            } finally {
                if (isMounted) setLoading(false);
            }
        };

        fetchSalaryData();

        return () => {
            isMounted = false;
        };
    }, [email]);

    const getFileIcon = (type: string) => {
        switch (type) {
            case "pdf":
                return <FileText className="h-8 w-8 text-red-500" />;
            case "docx":
            case "doc":
                return <FileText className="h-8 w-8 text-primary" />;
            case "xlsx":
            case "xls":
                return <FileText className="h-8 w-8 text-green-500" />;
            case "pptx":
            case "ppt":
                return <FileText className="h-8 w-8 text-orange-500" />;
            case "jpg":
            case "png":
            case "gif":
                return <FileImage className="h-8 w-8 text-purple-500" />;
            case "mp4":
            case "avi":
            case "mov":
                return <FileVideo className="h-8 w-8 text-pink-500" />;
            case "mp3":
            case "wav":
                return <FileAudio className="h-8 w-8 text-yellow-500" />;
            case "zip":
            case "rar":
                return <FileArchive className="h-8 w-8 text-gray-500" />;
            default:
                return <File className="h-8 w-8 text-gray-500" />;
        }
    };

    const handleViewFile = (document: Document) => {
        // if (document.file_url) {
        //   // If we have a direct URL, open in new tab
        //   window.open(document.file_url, '_blank');
        // } else
        if (document.content_base64) {
            // If we have base64 content, create a blob URL
            try {
                // Determine MIME type from file extension or attachment_type
                const mimeType = getMimeType(document.file_name, document.attachment_type);
                const byteCharacters = atob(document.content_base64);
                const byteNumbers = new Array(byteCharacters.length);

                for (let i = 0; i < byteCharacters.length; i++) {
                    byteNumbers[i] = byteCharacters.charCodeAt(i);
                }

                const byteArray = new Uint8Array(byteNumbers);
                const blob = new Blob([byteArray], { type: mimeType });
                const blobUrl = URL.createObjectURL(blob);

                window.open(blobUrl, '_blank');

                // Clean up the URL after some time
                setTimeout(() => URL.revokeObjectURL(blobUrl), 1000);
            } catch (error) {
                alert('Error: Unable to open file. The file format may be corrupted or unsupported.');
            }
        } else {
            alert('Error: No file content available for viewing.');
        }
    };

    const getMimeType = (fileName: string, attachmentType: string): string => {
        const extension = fileName.split('.').pop()?.toLowerCase();

        const mimeTypes: { [key: string]: string } = {
            'pdf': 'application/pdf',
            'jpg': 'image/jpeg',
            'jpeg': 'image/jpeg',
            'png': 'image/png',
            'gif': 'image/gif',
            'txt': 'text/plain',
            'doc': 'application/msword',
            'docx': 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
            'xls': 'application/vnd.ms-excel',
            'xlsx': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        };

        return mimeTypes[extension || ''] || 'application/octet-stream';
    };
    const handleDocumentAction = (action: string, documentId: string) => {
        switch (action) {
            case "view":
                handleViewFile(documents.find(doc => doc.file_name === documentId)!);
                console.log("View document:", documentId);
                break;
            case "download":
                console.log("Download document:", documentId);
                break;
            // case "favorite":
            //   setDocuments((prev) =>
            //     prev.map((doc) =>
            //       doc.id === documentId
            //         ? { ...doc, isFavorite: !doc.isFavorite }
            //         : doc
            //     )
            //   );
            //   break;
            case "share":
                console.log("Share document:", documentId);
                break;
            case "edit":
                console.log("Edit document:", documentId);
                break;
            case "delete":
                console.log("Delete document:", documentId);
                break;
        }
    };

    const DocumentsSkeleton = () => (
        <div className="w-full space-y-6 animate-fade-in">
            {/* Header Skeleton */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div className="space-y-2">
                    <Skeleton className="h-9 w-48" />
                    <Skeleton className="h-5 w-64" />
                </div>
                <div className="flex items-center gap-2">
                    <Skeleton className="h-9 w-24" />
                    <Skeleton className="h-9 w-32" />
                </div>
            </div>

            {/* Filters Card Skeleton */}
            <Card>
                <CardContent className="p-4">
                    <div className="flex flex-col lg:flex-row gap-4">
                        <Skeleton className="h-10 flex-1" />
                        <Skeleton className="h-10 w-40" />
                        <Skeleton className="h-10 w-40" />
                        <Skeleton className="h-10 w-40" />
                    </div>
                </CardContent>
            </Card>

            {/* Documents Grid Skeleton */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {[...Array(6)].map((_, i) => (
                    <Card key={i} className="overflow-hidden">
                        <CardHeader className="pb-4">
                            <div className="flex items-start justify-between">
                                <div className="flex items-center gap-3 flex-1">
                                    <Skeleton className="h-8 w-8 rounded" />
                                    <div className="flex-1 space-y-2">
                                        <Skeleton className="h-5 w-full" />
                                        <Skeleton className="h-4 w-20" />
                                    </div>
                                </div>
                                <Skeleton className="h-8 w-8" />
                            </div>
                        </CardHeader>
                        <CardContent className="space-y-3">
                            <div className="flex items-center justify-between">
                                <Skeleton className="h-4 w-16" />
                                <Skeleton className="h-4 w-24" />
                            </div>
                            <div className="flex gap-2">
                                <Skeleton className="h-9 flex-1" />
                                <Skeleton className="h-9 w-9" />
                            </div>
                        </CardContent>
                    </Card>
                ))}
            </div>
        </div>
    );

    if (loading || !filteredDocuments.length) {
        return <DocumentsSkeleton />;
    }

    return (
        <div className="w-full space-y-6">
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div>
                    <h1 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold leading-tight">
                        {t("documents.title")}
                    </h1>
                    <p className="text-muted-foreground mt-2 sm:text-lg">{t("documents.description")}</p>
                </div>
            </div>
            <div
                className={viewMode === "grid" ? "grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4" : "space-y-2"}>
                {filteredDocuments.map((doc) => (
                    <Card
                        key={doc.file_name}
                        className='cursor-pointer transition-all hover:shadow-md'>
                        <CardContent className="p-4">
                            {viewMode === "grid" ? (
                                // Grid View
                                <div className="space-y-3">
                                    <div className="flex items-start justify-between">
                                        {getFileIcon(doc.attachment_type)}
                                    </div>

                                    <div className="space-y-1">
                                        <h3
                                            className="font-medium text-sm line-clamp-2"
                                            title={doc.file_name}
                                        >
                                            {doc.file_name}
                                        </h3>
                                        <p className="text-xs text-muted-foreground line-clamp-1">
                                            {doc.file_name}
                                        </p>
                                    </div>

                                    <div className="flex items-center justify-between">
                                        <Badge variant="secondary" className="text-xs">
                                            {doc.attachment_type}
                                        </Badge>
                                        <div className="flex items-center gap-1">
                                            <Button
                                                variant="ghost"
                                                size="sm"
                                                className="h-6 w-6 p-0"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    handleDocumentAction("view", doc.file_name);
                                                }}
                                            >
                                                <Eye className="h-3 w-3" />
                                            </Button>
                                        </div>
                                    </div>
                                </div>
                            ) : (
                                // List View
                                <div className="flex items-center gap-4">
                                    <div className="flex items-center gap-3 flex-1 min-w-0">
                                        {getFileIcon(doc.attachment_type)}
                                        <div className="flex-1 min-w-0">
                                            <h3 className="font-medium text-sm truncate">
                                                {doc.file_name}
                                            </h3>
                                            <p className="text-xs text-muted-foreground truncate">
                                                {doc.attachment_type}
                                            </p>
                                        </div>
                                    </div>

                                    <div className="hidden sm:flex items-center gap-4 text-sm text-muted-foreground">
                                        <span>{doc.attachment_type}</span>
                                    </div>

                                </div>
                            )}
                        </CardContent>
                    </Card>
                ))}
            </div>
        </div>
    );
};

export default MyDocuments;
